import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Spinner,
  Button,
  Flex,
  VStack,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { quickbooksService } from '../../api/quickbooks';

const SyncJobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: detailsModalOpen, onOpen: openDetailsModal, onClose: closeDetailsModal } = useDisclosure();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customerDetailsMap, setCustomerDetailsMap] = useState({});

  // Get customer list from job details
  const customerList = useMemo(() => {
    if (!job?.details) return [];

    if (job.details.customer_ids && Array.isArray(job.details.customer_ids)) {
      return job.details.customer_ids;
    }
    if (job.details.customer_id) {
      return [job.details.customer_id];
    }
    return [];
  }, [job?.details]);

  // Map results by customer for easy lookup
  const resultsByCustomer = useMemo(() => {
    return job?.results || {};
  }, [job?.results]);

  // Get overall status for a customer
  const getCustomerOverallStatus = useCallback((customerId) => {
    const tasks = resultsByCustomer[customerId];
    if (!tasks) {
      return "in_progress";
    }

    // Only consider tasks that actually exist and have a status
    const statuses = Object.values(tasks)
      .filter(task => task && task.status)
      .map(task => task.status);

    if (statuses.length === 0) {
      return "in_progress";
    }

    if (statuses.some(status => status === 'in_progress' || status === 'pending')) {
      return "in_progress";
    }
    if (statuses.some(status => status === 'failed')) {
      return "failed";
    }
    if (statuses.every(status => status === 'success' || status === 'completed')) {
      return "success";
    }

    return "in_progress";
  }, [resultsByCustomer]);

  // Calculate overall job status based on customer results
  const getOverallJobStatus = useCallback(() => {
    if (!job || !customerList.length) {
      return job?.status || 'pending';
    }

    // Check all customer statuses
    const customerStatuses = customerList.map(customerId => getCustomerOverallStatus(customerId));

    // If any customers are still in progress, job is in progress
    if (customerStatuses.some(status => status === 'in_progress')) {
      return 'in_progress';
    }

    // If any customers failed, job has failures
    if (customerStatuses.some(status => status === 'failed')) {
      return 'failed';
    }

    // If all customers succeeded, job is complete
    if (customerStatuses.every(status => status === 'success')) {
      return 'completed';
    }

    // Fallback to backend status
    return job?.status || 'pending';
  }, [job, customerList, getCustomerOverallStatus]);

  // Check if the job is still in progress (use calculated status)
  const isJobActive = useMemo(() => {
    const overallStatus = getOverallJobStatus();
    return ['pending', 'in_progress'].includes(overallStatus);
  }, [getOverallJobStatus]);

  // Get customer name from results or customer list
  const getCustomerName = useCallback((customerId) => {
    const idKey = String(customerId);

    // Check if we've cached the customer details already
    const cachedCustomer = customerDetailsMap[idKey];
    if (cachedCustomer?.name) {
      return cachedCustomer.name;
    }

    // First check if we have customer details in the results
    if (job?.results?.[customerId]?.customerDetails) {
      return job.results[customerId].customerDetails.name || `Customer ${customerId.substring(0, 8)}`;
    }

    // Default fallback
    return `Customer ${customerId.substring(0, 8)}`;
  }, [job?.results, customerDetailsMap]);

  // Get job sync types
  const getJobSyncTypes = useCallback(() => {
    if (!job?.details) return "N/A";

    // Check for activity types in results structure (like the sync history does)
    const activityTypes = new Set();
    if (job.results) {
      Object.values(job.results).forEach(customerResults => {
        if (Array.isArray(customerResults)) {
          // Handle array format like sync history
          customerResults.forEach(activity => {
            if (activity.type) {
              activityTypes.add(activity.type);
            }
          });
        } else if (typeof customerResults === 'object' && customerResults !== null) {
          // Handle object format where keys are activity types
          Object.keys(customerResults).forEach(key => {
            activityTypes.add(key);
          });
        }
      });
    }

    // Convert activity types to display names (matching sync history logic)
    const displayTypes = [];
    if (activityTypes.has('general_ledger_cdc')) displayTypes.push('GL Updates');
    if (activityTypes.has('general_ledger_fetch')) displayTypes.push('GL Fetch');
    if (activityTypes.has('general_ledger_build')) displayTypes.push('GL Full');
    if (activityTypes.has('general_ledger')) displayTypes.push('GL Range');
    if (activityTypes.has('trial_balance')) displayTypes.push('TB Balances');
    if (activityTypes.has('opening_balances')) displayTypes.push('GL Balances');

    if (displayTypes.length > 0) {
      return displayTypes.join(', ');
    }

    // Check legacy operation_type field (like sync history)
    if (job.details?.operation_type) {
      switch (job.details.operation_type) {
        case "general_ledger_cdc": return "GL Updates";
        case "general_ledger": return "GL Range";
        case "trial_balance": return "TB Balances";
        case "general_ledger_fetch": return "GL Fetch";
        case "general_ledger_build": return "GL Full";
        case "opening_balances": return "GL Balances";
        default: return job.details.operation_type;
      }
    }

    // Fallback to manifest flags for older workflows
    const types = [];
    if (job.details.sync_transactions) types.push("GL");
    if (job.details.sync_trial_balance) types.push("TB");

    return types.length > 0 ? types.join(", ") : "N/A";
  }, [job]);

  // Render status badge
  const renderStatusBadge = useCallback((status) => {
    let color;
    let text;

    switch (status) {
      case 'success':
      case 'completed':
        color = "green";
        text = "Completed Successfully";
        break;
      case 'failed':
        color = "red";
        text = "Completed With Errors";
        break;
      case 'in_progress':
        color = "blue";
        text = "In Progress";
        break;
      default:
        color = "gray";
        text = "Pending";
    }

    return <Badge colorScheme={color}>{text}</Badge>;
  }, []);

  // Fetch job details
  const fetchJobDetails = useCallback(async () => {
    if (!jobId) return;

    try {
      const response = await quickbooksService.getJobDetails(jobId);
      const jobData = response.data;

      setJob(jobData);
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast({
        title: 'Error',
        description: 'Could not load the requested sync job.',
        status: 'error',
        duration: 10000,
        isClosable: true
      });

      // Navigate back if job not found
      if (error.response && error.response.status === 404) {
        navigate('/quickbooks-sync-history');
      }
    } finally {
      setLoading(false);
    }
  }, [jobId, toast, navigate]);

  // Ensure we have detailed customer information for every ID associated with the job
  useEffect(() => {
    if (!job) return;

    const candidateIds = new Set();

    if (job.details?.customer_ids && Array.isArray(job.details.customer_ids)) {
      job.details.customer_ids.forEach((id) => candidateIds.add(String(id)));
    }

    if (job.details?.customer_id) {
      candidateIds.add(String(job.details.customer_id));
    }

    Object.keys(job.results || {}).forEach((resultCustomerId) => {
      candidateIds.add(String(resultCustomerId));
    });

    // Cache any customer details provided in the job results immediately
    const jobProvidedCustomers = [];
    candidateIds.forEach((id) => {
      const detailsFromResult = job.results?.[id]?.customerDetails;
      if (detailsFromResult?.name) {
        jobProvidedCustomers.push({
          ...detailsFromResult,
          id: detailsFromResult.id ? String(detailsFromResult.id) : id,
        });
      }
    });

    if (jobProvidedCustomers.length > 0) {
      setCustomerDetailsMap((prev) => {
        const next = { ...prev };
        let changed = false;

        jobProvidedCustomers.forEach((customer) => {
          const key = String(customer.id);
          if (!next[key] || next[key].name !== customer.name) {
            next[key] = customer;
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    }

    const idsToFetch = Array.from(candidateIds).filter((id) => {
      if (customerDetailsMap[id]) return false;
      const detailsFromResult = job.results?.[id]?.customerDetails;
      return !(detailsFromResult && detailsFromResult.name);
    });

    if (idsToFetch.length === 0) {
      return;
    }

    let isCancelled = false;

    const fetchMissingCustomers = async () => {
      try {
        const responses = await Promise.allSettled(
          idsToFetch.map((id) => quickbooksService.getCustomerById(id))
        );

        if (isCancelled) {
          return;
        }

        const fetchedCustomers = responses
          .filter((result) => result.status === "fulfilled" && result.value?.data)
          .map((result) => result.value.data)
          .filter((customer) => customer?.id);

        if (fetchedCustomers.length === 0) {
          return;
        }

        setCustomerDetailsMap((prev) => {
          const next = { ...prev };
          let changed = false;

          fetchedCustomers.forEach((customer) => {
            const key = String(customer.id);
            if (!next[key] || next[key].name !== customer.name) {
              next[key] = customer;
              changed = true;
            }
          });

          return changed ? next : prev;
        });
      } catch (error) {
        console.error("Error fetching customer details for job:", error);
      }
    };

    fetchMissingCustomers();

    return () => {
      isCancelled = true;
    };
  }, [job, customerDetailsMap]);

  // Initial fetch
  useEffect(() => {
    setLoading(true);
    fetchJobDetails();
  }, [fetchJobDetails]);

  // Poll for updates if job is active
  useEffect(() => {
    if (!jobId || !isJobActive) return;

    console.log('Starting polling for sync job:', jobId);
    const interval = setInterval(fetchJobDetails, 3000); // Poll every 3 seconds

    return () => clearInterval(interval);
  }, [jobId, isJobActive, fetchJobDetails]);

  // Handle show details
  const handleShowDetails = useCallback((customerId) => {
    setSelectedCustomerId(customerId);
    openDetailsModal();
  }, [openDetailsModal]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate('/quickbooks-sync-history');
  }, [navigate]);

  if (loading) {
    return (
      <Container maxW="container.xl" pt={"80px"}>
        <Stack py={8}>
          <Card>
            <CardHeader pb={0}>
              <Flex justify="space-between" align="center">
                <Heading size="lg">Loading Sync Job Details</Heading>
                <Button onClick={handleBack} leftIcon={<ChevronLeftIcon />}>
                  Back
                </Button>
              </Flex>
            </CardHeader>
            <CardBody>
              <Flex direction="column" align="center" py={8}>
                <Spinner size="xl" mb={4} />
                <Text fontSize="lg">Loading sync job details...</Text>
              </Flex>
            </CardBody>
          </Card>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" pt={"80px"}>
      <Stack py={8}>
        <Card>
          <CardHeader pb={0}>
            <Flex justify="space-between" align="center">
              <Box>
                <Heading size="lg">Sync Job Details</Heading>
                <Text mt={2} color="gray.600">
                  Job ID: {jobId}
                </Text>
                {job?.started_at && (
                  <Text fontSize="sm" color="gray.500">
                    Started: {new Date(job.started_at).toLocaleString()}
                  </Text>
                )}
              </Box>
              <Button onClick={handleBack} leftIcon={<ChevronLeftIcon />}>
                Back
              </Button>
            </Flex>
          </CardHeader>

          <CardBody>
            <VStack spacing={6} align="stretch" pt={4}>
              {/* Job Status */}
              <Box>
                <Heading size="md" mb={4}>Sync Job Status</Heading>
                {isJobActive ? (
                  <Flex direction="column" align="center" py={8}>
                    <Spinner size="xl" mb={4} />
                    <Text fontSize="lg">
                      Processing sync job for {customerList.length} customer{customerList.length !== 1 ? 's' : ''}...
                    </Text>
                    <Text fontSize="md" color="gray.600" mt={2}>
                      Sync Types: {getJobSyncTypes()}
                    </Text>
                  </Flex>
                ) : (
                  <Flex align="center" mb={4}>
                    <Text fontSize="lg" mr={4}>Job Status:</Text>
                    {renderStatusBadge(getOverallJobStatus())}
                    <Text fontSize="md" color="gray.600" ml={4}>
                      Sync Types: {getJobSyncTypes()}
                    </Text>
                  </Flex>
                )}
              </Box>

              {/* Customer Status Table */}
              {customerList.length > 0 && (
                <Box>
                  <Heading size="md" mb={4}>Customer Status</Heading>
                  <Box borderWidth={1} borderRadius="md">
                    <Table variant="simple" size="sm">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Customer</Th>
                          <Th>Sync Types</Th>
                          <Th>Status</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {customerList.map((customerId) => (
                          <Tr key={customerId}>
                            <Td>{getCustomerName(customerId)}</Td>
                            <Td>{getJobSyncTypes()}</Td>
                            <Td>{renderStatusBadge(getCustomerOverallStatus(customerId))}</Td>
                            <Td>
                              <Button
                                size="sm"
                                onClick={() => handleShowDetails(customerId)}
                                disabled={!resultsByCustomer[customerId]}
                              >
                                Details
                              </Button>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Details Modal */}
        <Modal isOpen={detailsModalOpen} onClose={closeDetailsModal} isCentered size="2xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Sync Task Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={8} align="stretch">
                {selectedCustomerId && resultsByCustomer[selectedCustomerId] &&
                  Object.entries(resultsByCustomer[selectedCustomerId]).map(([taskType, taskResult]) => {
                    if (!taskResult || typeof taskResult !== 'object' || !taskResult.status) return null;

                    // Map activity types to descriptive labels
                    let label;
                    if (taskType === 'general_ledger_cdc') {
                      label = 'CDC Sync';
                    } else if (taskType === 'general_ledger') {
                      label = 'General Ledger';
                    } else if (taskType === 'trial_balance') {
                      label = 'Trial Balance';
                    } else if (taskType === 'general_ledger_fetch') {
                      label = 'Fetch General Ledger Dump';
                    } else if (taskType === 'general_ledger_build') {
                      label = 'Import General Ledger Dump';
                    } else if (taskType === 'opening_balances') {
                      label = 'Opening Balances';
                    } else {
                      label = taskType;
                    }

                    const status = taskResult.status;
                    const errors = taskResult.errors;

                    return (
                      <Box key={taskType}>
                        <Heading size="md" mb={2}>{label}</Heading>
                        <Badge colorScheme={status === 'success' ? 'green' : status === 'failed' ? 'red' : 'gray'} mb={2} fontSize="md">
                          {status ? status.toUpperCase() : 'N/A'}
                        </Badge>

                        {status === 'failed' && errors && (
                          <Box
                            mt={4}
                            p={4}
                            bg="red.50"
                            borderRadius="md"
                            border="1px solid"
                            borderColor="red.200"
                            borderLeftWidth={4}
                            borderLeftColor="red.400"
                            color="red.800"
                            fontFamily="mono"
                            fontSize="md"
                            lineHeight={1.7}
                          >
                            {(() => {
                              let errorBlocks = [];
                              if (Array.isArray(errors)) {
                                errorBlocks = errors.map((e, idx) => {
                                  if (typeof e === 'object') {
                                    return (
                                      <Box key={idx} mb={2}>
                                        <Box fontWeight="bold">{e.error}</Box>
                                        {e.detail && (
                                          <Box pl={4} color="gray.700">
                                            {Array.isArray(e.detail) ? (
                                              e.detail.map((d, i) => (
                                                <Box key={i} as="div" fontSize="sm">- {d}</Box>
                                              ))
                                            ) : (
                                              <Box as="div" fontSize="sm">- {e.detail}</Box>
                                            )}
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  }
                                  return <Box key={idx} mb={2}>{String(e)}</Box>;
                                });
                              } else if (typeof errors === 'string') {
                                errorBlocks = errors.split(/\r?\n/).filter(line => line.trim() !== '').map((line, idx) => (
                                  <Box key={idx} mb={2}>{line}</Box>
                                ));
                              }
                              return errorBlocks;
                            })()}
                          </Box>
                        )}

                        {taskResult.processed_count !== undefined && (
                          <Text fontSize="sm" color="gray.600" mt={2}>
                            Processed: {taskResult.processed_count} items
                          </Text>
                        )}
                      </Box>
                    );
                  })
                }
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Stack>
    </Container>
  );
};

export default SyncJobDetails; 